/** Stop a bad device clock turning fresh auth sessions into a refresh storm.
 * Only inspect successful session responses from our own Supabase Auth origin.
 * Neither JWTs nor server-side expiry/authorization are changed.
 */
export const CLOCK_SKEW_ERROR = 'device_clock_skew';
export const CLOCK_SKEW_MESSAGE = 'Your device’s date or time is incorrect. Turn on automatic date and time in your device settings, sync the clock, then sign in again.';
/** auth-js treats a session as expired once fewer than 90s remain
 * (EXPIRY_MARGIN_MS = 3 ticks x 30s). 120s keeps a little headroom. */
const EXPIRY_HEADROOM_SECONDS = 120;
const MIN_REJECT_SKEW_SECONDS = 5 * 60;
let detected = false;
const listeners = new Set<() => void>();
export const getClockSkew = () => detected;
export const subscribeClockSkew = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
function reportClockSkew(value: boolean) {
  if (detected === value) return;
  detected = value;
  listeners.forEach((listener) => listener());
}

export function withAuthClockCheck(baseUrl: string, transport: typeof fetch): typeof fetch {
  const authUrl = new URL('/auth/v1/', baseUrl);
  return async (input, init) => {
    const response = await transport(input, init);
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
    if (!response.ok || url.origin !== authUrl.origin || !url.pathname.startsWith(authUrl.pathname)) return response;
    // expires_at is server time, while expires_in is the session's lifetime.
    // Unlike the Date header these are available even without CORS exposure.
    let body: { access_token?: unknown; expires_at?: unknown; expires_in?: unknown };
    try { body = await response.clone().json(); } catch { return response; }
    if (!body || typeof body.access_token !== 'string' ||
      typeof body.expires_at !== 'number' || !Number.isFinite(body.expires_at) ||
      typeof body.expires_in !== 'number' || !Number.isFinite(body.expires_in) || body.expires_in <= 0) return response;
    const issuedAt = body.expires_at - body.expires_in;
    const skew = Math.abs(Date.now() / 1000 - issuedAt);
    // Only a skew close to the whole session lifetime is fatal. Ahead, auth-js
    // sees the fresh session as already expired and refreshes it in a loop;
    // behind, it keeps sending an expired JWT for most of every hour. A clock
    // a few minutes (or tens of minutes) off still works and must not be
    // locked out, so smaller skew passes through untouched.
    // The 5-minute floor keeps a very short JWT lifetime from rejecting every clock.
    const unusable = skew >= Math.max(body.expires_in - EXPIRY_HEADROOM_SECONDS, MIN_REJECT_SKEW_SECONDS);
    reportClockSkew(unusable);
    if (!unusable) return response;
    // A non-retryable auth error prevents auth-js from persisting this session
    // (or endlessly rotating refresh tokens). Existing sessions are cleared by
    // auth-js's normal failure path. Retry after clock sync obtains a fresh one.
    return new Response(JSON.stringify({ code: CLOCK_SKEW_ERROR, error_code: CLOCK_SKEW_ERROR, message: CLOCK_SKEW_MESSAGE }), {
      status: 400,
      headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-store' },
    });
  };
}
